'use client'

import { cn } from '@repo/ui/lib/utils'
import json from '@shikijs/langs/json'
import githubDark from '@shikijs/themes/github-dark'
import githubLight from '@shikijs/themes/github-light'
import { useEffect, useState } from 'react'
import { createHighlighterCore, type HighlighterCore, type ShikiTransformer } from 'shiki/core'
import { createOnigurumaEngine } from 'shiki/engine/oniguruma'

import './CodeFormattingStyles.css'

export type FormattedCodeProps = {
  code: string
  language?: string
  theme?: 'light' | 'dark' | 'muted'
  showLineNumbers?: boolean
  hasTitle?: boolean
  wrapLines?: boolean
}

/**
 * Shiki with only the grammar and themes this component renders.
 *
 * Every caller passes `json` or no language at all, and an unregistered language falls back to
 * plain text rather than failing, so nothing else is loaded. Registering a language here is a
 * deliberate act: the alternative is Shiki's bundled entry point, which registers every grammar it
 * ships as a dynamic import and makes the bundler emit a chunk per language whether or not
 * anything renders it.
 *
 * Built once and shared, because building a highlighter compiles its grammars.
 */
let highlighterPromise: Promise<HighlighterCore> | null = null

function getHighlighter() {
  highlighterPromise ??= createHighlighterCore({
    langs: [json],
    themes: [githubLight, githubDark],
    engine: createOnigurumaEngine(() => import('shiki/wasm')),
  })

  return highlighterPromise
}

/**
 * The markup `CodeFormattingStyles.css` is written against.
 *
 * Line numbers are drawn by a CSS counter on `[data-line]`, not written into the markup, so each
 * line needs the attribute and nothing more. An empty line keeps a space: without one it collapses
 * to zero height and cannot be selected, which also breaks copy and paste of a blank line.
 */
const lineTransformer: ShikiTransformer = {
  name: 'fuze-code-block',
  line(node) {
    node.properties['data-line'] = ''

    if (node.children.length === 0) {
      node.children = [{ type: 'text', value: ' ' }]
    }

    return node
  },
  pre(node) {
    // The surrounding panel provides the background; the theme supplies only the foreground colours.
    const style = String(node.properties.style ?? '')
    node.properties.style = style
      .split(';')
      .filter((rule) => rule.trim() && !rule.trim().startsWith('background-color'))
      .join(';')

    return node
  },
  code(node) {
    // Each line is a grid row. This is load-bearing twice over: it puts every line on its own row
    // whatever the stylesheet does with `display`, and a grid container ignores the whitespace-only
    // text nodes the highlighter writes between lines, which would otherwise render as a blank line
    // between every line once `[data-line]` becomes a block under `data-wrap-lines`.
    const style = String(node.properties.style ?? '')
    node.properties.style = [style.trim().replace(/;$/, ''), 'display: grid']
      .filter(Boolean)
      .join(';')

    return node
  },
}

export function FormattedCode({
  code,
  language,
  theme = 'muted',
  showLineNumbers = false,
  hasTitle,
  wrapLines = false,
}: FormattedCodeProps) {
  const [highlightedCode, setHighlightedCode] = useState('')

  useEffect(() => {
    let isCurrent = true

    const highlightCode = async () => {
      try {
        const highlighter = await getHighlighter()

        // Count lines to determine digit length for line numbers
        const lineCount = code.split('\n').length
        const digitCount = lineCount.toString().length

        let html = highlighter.codeToHtml(code, {
          // A language the highlighter does not carry renders as plain text rather than throwing.
          lang: language && highlighter.getLoadedLanguages().includes(language) ? language : 'text',
          theme: theme === 'light' || theme === 'muted' ? 'github-light' : 'github-dark',
          transformers: [lineTransformer],
        })

        // Add line numbers with the correct max-digits attribute if showLineNumbers is true
        if (showLineNumbers) {
          html = html.replace(
            '<code',
            `<code data-line-numbers data-line-numbers-max-digits="${digitCount}"`,
          )
        }

        // Add wrap-lines attribute if wrapLines is true
        if (wrapLines) {
          html = html.replace('<pre', '<pre data-wrap-lines')
        }

        // `figure` carries the horizontal scrolling and vertical padding in the stylesheet.
        if (isCurrent) setHighlightedCode(`<figure>${html}</figure>`)
      } catch (error) {
        console.error('Error highlighting code:', error)
        if (isCurrent) setHighlightedCode(`<pre><code>${code}</code></pre>`)
      }
    }

    highlightCode()

    return () => {
      isCurrent = false
    }
  }, [code, language, theme, showLineNumbers, wrapLines])

  return (
    <div
      className={cn('code-block relative overflow-hidden px-4 text-sm', !hasTitle && 'mr-12')}
      dangerouslySetInnerHTML={{
        __html: highlightedCode,
      }}
    />
  )
}
